import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Users, Sparkles, ChevronDown, FileText, Target, CheckCircle2, Send } from "lucide-react";
import api from "../lib/api";
import AdminNav from "../components/AdminNav";

/**
 * Admin-only list of signed-up users (accounts with a real email set —
 * anonymous devices live in /admin/orphans instead). Per-user roll-ups:
 * total sessions, last session date, and AI minutes generated this month.
 *
 * Auth enforcement is entirely backend-side — routers/admin.py gates on
 * ADMIN_EMAILS. The page gracefully renders 403/404 so a non-admin who
 * lands here still sees a clean explainer instead of a blank screen.
 */
export default function AdminUsers() {
  const [state, setState] = useState({ status: "loading", users: [], total: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({}); // { userId: { status, data } }

  const load = async () => {
    try {
      const r = await api.get("/admin/users?limit=500");
      setState({
        status: "ok",
        users: r.data.users || [],
        total: r.data.total ?? (r.data.users || []).length,
      });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) setState({ status: "forbidden", users: [], total: 0 });
      else if (code === 404) setState({ status: "disabled", users: [], total: 0 });
      else {
        console.error("admin users load failed", e);
        setState({ status: "error", users: [], total: 0 });
      }
    }
  };

  const loadDetail = async (userId) => {
    if (details[userId]?.status === "ok" || details[userId]?.status === "loading") return;
    setDetails((d) => ({ ...d, [userId]: { status: "loading" } }));
    try {
      const r = await api.get(`/admin/users/${userId}`);
      setDetails((d) => ({ ...d, [userId]: { status: "ok", data: r.data } }));
    } catch (e) {
      console.error("admin user detail load failed", e);
      setDetails((d) => ({ ...d, [userId]: { status: "error" } }));
    }
  };

  const toggleExpand = (userId) => {
    if (expandedId === userId) {
      setExpandedId(null);
    } else {
      setExpandedId(userId);
      loadDetail(userId);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso.slice(0, 10);
    }
  };

  const planStyle = (plan) => {
    if (plan === "syndicate") return "text-[#c89f45] border-[#c89f45]/40 bg-[#c89f45]/10";
    if (plan === "pro") return "text-[#c89f45] border-[#c89f45]/40";
    return "text-[#888] border-[#2a2a2a]";
  };

  return (
    <>
      <AdminNav />
      <div className="max-w-6xl mx-auto px-6 pb-16" data-testid="admin-users-page">
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-serif text-4xl">Registered users</h1>
            <p className="text-sm text-[#888] max-w-2xl mt-2 leading-relaxed">
              Every account with a signed-in Google identity — newest first.
              Anonymous devices don't appear here; you'll find their lost
              recordings on the Orphans tab.
            </p>
          </div>
          <BulkNudgeButton onDone={load} />
        </div>

        {state.status === "loading" && (
          <div
            className="surface rounded-md p-10 mt-8 text-center text-sm text-[#888]"
            data-testid="admin-users-loading"
          >
            Loading…
          </div>
        )}

        {state.status === "forbidden" && (
          <div
            className="surface rounded-md p-8 mt-8 border-red-900/40 text-sm text-[#eaeaea] flex gap-3 items-start"
            data-testid="admin-users-forbidden"
          >
            <AlertTriangle size={18} className="text-red-400 mt-0.5" />
            <div>
              <p className="text-base">Not authorised.</p>
              <p className="text-[#888] mt-1">
                This console is limited to accounts in{" "}
                <code className="text-[#c89f45]">ADMIN_EMAILS</code>. Sign in
                with an admin account first.
              </p>
            </div>
          </div>
        )}

        {state.status === "disabled" && (
          <div
            className="surface rounded-md p-8 mt-8 text-sm text-[#eaeaea] flex gap-3 items-start"
            data-testid="admin-users-disabled"
          >
            <AlertTriangle size={18} className="text-[#c89f45] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-base">Admin console disabled on this environment.</p>
              <p className="text-[#888] mt-2">
                This page unlocks once the backend env has an{" "}
                <code className="text-[#c89f45]">ADMIN_EMAILS</code> allow-list.
                Add the following key (comma-separated for multiple admins) and
                redeploy:
              </p>
              <pre className="mt-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 font-mono text-[11px] text-[#c89f45] overflow-x-auto">
                ADMIN_EMAILS=admin@foundtheroom.com
              </pre>
              <p className="text-[11px] text-[#666] mt-2 font-mono">
                Preview vars don't auto-copy to prod. If you can't find where
                to set backend env on{" "}
                <code className="text-[#888]">foundtheroom.com</code>, ping
                Emergent Support with the line above.
              </p>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div
            className="surface rounded-md p-8 mt-8 text-sm text-[#eaeaea]"
            data-testid="admin-users-error"
          >
            Could not load users.{" "}
            <button
              className="text-[#c89f45] underline"
              onClick={load}
              data-testid="admin-users-retry"
            >
              Retry
            </button>
          </div>
        )}

        {state.status === "ok" && state.users.length === 0 && (
          <div
            className="surface rounded-md p-10 mt-8 text-center"
            data-testid="admin-users-empty"
          >
            <Sparkles size={20} className="text-[#c89f45] mx-auto mb-3" />
            <p className="text-base">No signed-up users yet.</p>
            <p className="text-sm text-[#888] mt-1">
              Once someone completes Google sign-in, they'll show up here.
            </p>
          </div>
        )}

        {state.status === "ok" && state.users.length > 0 && (
          <>
            <div className="flex items-center justify-between mt-8 mb-3">
              <p
                className="text-xs font-mono uppercase tracking-widest text-[#888]"
                data-testid="admin-users-count"
              >
                <Users size={12} className="inline mr-1 -mt-0.5" />
                {state.total} REGISTERED
                {state.users.length < state.total && (
                  <span className="text-[#4a4a4a]"> · SHOWING {state.users.length}</span>
                )}
              </p>
              <button
                className="text-xs font-mono uppercase tracking-widest text-[#888] hover:text-[#c89f45]"
                onClick={load}
                data-testid="admin-users-refresh"
              >
                REFRESH
              </button>
            </div>

            <div className="surface rounded-md overflow-hidden" data-testid="admin-users-list">
              <div className="hidden md:grid grid-cols-[minmax(0,1fr)_88px_110px_140px_60px_16px] gap-6 px-5 py-3 border-b border-[#1a1a1a] text-[10px] font-mono uppercase tracking-widest text-[#666]">
                <div>USER</div>
                <div className="text-center">PLAN</div>
                <div className="text-right">SIGNED UP</div>
                <div className="text-right">SESSIONS</div>
                <div className="text-right">AI · MTD</div>
                <div />
              </div>

              <ul>
                {state.users.map((u) => {
                  const planLabel = u.billing_cycle === "founder" ? "FOUNDER" : (u.plan || "free").toUpperCase();
                  const isOpen = expandedId === u.id;
                  const detail = details[u.id];
                  return (
                  <li
                    key={u.id}
                    className="border-b border-[#1a1a1a] last:border-0"
                    data-testid={`admin-user-${u.id}`}
                  >
                  <button
                    type="button"
                    onClick={() => toggleExpand(u.id)}
                    aria-expanded={isOpen}
                    aria-controls={`admin-user-detail-${u.id}`}
                    className={`w-full text-left flex flex-col gap-3 md:grid md:grid-cols-[minmax(0,1fr)_88px_110px_140px_60px_16px] md:gap-6 px-5 py-4 md:items-start transition-colors ${isOpen ? "bg-[#101010]" : "hover:bg-[#101010]"}`}
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    {/* User cell (name + avatar). On mobile, the plan pill floats to the right. */}
                    <div className="flex items-center gap-3 min-w-0">
                      {u.picture ? (
                        <img
                          src={u.picture}
                          alt=""
                          className="h-8 w-8 rounded-sm object-cover shrink-0"
                        />
                      ) : (
                        <span className="h-8 w-8 rounded-sm bg-[#121212] flex items-center justify-center text-[#c89f45] font-serif text-sm shrink-0">
                          {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{u.name || "—"}</p>
                        <p className="text-[11px] font-mono text-[#888] truncate">
                          {u.email}
                        </p>
                      </div>
                      {/* Mobile-only plan pill next to name */}
                      <span
                        className={`md:hidden shrink-0 text-[9px] font-mono uppercase tracking-widest border rounded px-1.5 py-0.5 ${planStyle(u.plan)}`}
                        data-testid={`admin-user-plan-mobile-${u.id}`}
                      >
                        {planLabel}
                      </span>
                      {/* Mobile-only chevron */}
                      <ChevronDown
                        size={16}
                        className={`md:hidden shrink-0 text-[#666] transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </div>

                    {/* Desktop-only plan cell */}
                    <div className="hidden md:block md:text-center md:pt-1">
                      <span
                        className={`inline-block text-[9px] font-mono uppercase tracking-widest border rounded px-1.5 py-0.5 ${planStyle(u.plan)}`}
                        data-testid={`admin-user-plan-${u.id}`}
                      >
                        {planLabel}
                      </span>
                    </div>

                    {/* Signed up */}
                    <div className="hidden md:block md:text-right md:pt-1 text-[11px] font-mono text-[#888]">
                      {fmtDate(u.created_at)}
                    </div>

                    {/* Sessions */}
                    <div
                      className="hidden md:block md:text-right md:pt-1 text-[11px] font-mono leading-tight"
                      data-testid={`admin-user-sessions-${u.id}`}
                    >
                      <span className="text-[#eaeaea]">{u.total_sessions}</span>
                      {u.last_session_at && (
                        <span className="text-[#666] block mt-1">
                          last · {fmtDate(u.last_session_at)}
                        </span>
                      )}
                    </div>

                    {/* AI · MTD */}
                    <div
                      className="hidden md:block md:text-right md:pt-1 text-[11px] font-mono text-[#eaeaea]"
                      data-testid={`admin-user-ai-${u.id}`}
                    >
                      {u.ai_minutes_this_month || 0}
                    </div>

                    {/* Desktop chevron */}
                    <ChevronDown
                      size={16}
                      className={`hidden md:block md:mt-1 text-[#666] transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />

                    {/* Mobile-only labeled metadata line */}
                    <div className="md:hidden flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-[#888] pl-11">
                      <span>
                        <span className="text-[#555] uppercase tracking-widest text-[9px] mr-1">Joined</span>
                        {fmtDate(u.created_at)}
                      </span>
                      <span>
                        <span className="text-[#555] uppercase tracking-widest text-[9px] mr-1">Sessions</span>
                        <span className="text-[#eaeaea]">{u.total_sessions}</span>
                        {u.last_session_at && (
                          <span className="text-[#666]"> · last {fmtDate(u.last_session_at)}</span>
                        )}
                      </span>
                      <span>
                        <span className="text-[#555] uppercase tracking-widest text-[9px] mr-1">AI · MTD</span>
                        <span className="text-[#eaeaea]">{u.ai_minutes_this_month || 0}</span>
                      </span>
                    </div>
                  </button>

                  {isOpen && (
                    <UserDetailPanel
                      user={u}
                      detail={detail}
                      fmtDate={fmtDate}
                    />
                  )}
                  </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
}


function UserDetailPanel({ user, detail, fmtDate }) {
  return (
    <div
      id={`admin-user-detail-${user.id}`}
      className="bg-[#0d0d0d] border-t border-[#1a1a1a] px-5 py-5"
      data-testid={`admin-user-detail-${user.id}`}
    >
      {(!detail || detail.status === "loading") && (
        <div className="text-xs font-mono text-[#666]">Loading detail…</div>
      )}
      {detail?.status === "error" && (
        <div className="text-xs font-mono text-red-400">Could not load detail for this user.</div>
      )}
      {detail?.status === "ok" && (
        <div className="flex flex-col gap-5">
          {/* Roll-up chips */}
          <div className="flex flex-wrap gap-3">
            <RollupChip
              icon={Target}
              label="Active goals"
              value={detail.data.goals.active}
              secondary={`${detail.data.goals.total} total`}
              testId={`admin-user-goals-${user.id}`}
            />
            <RollupChip
              icon={CheckCircle2}
              label="Actions signed off"
              value={detail.data.action_items.done}
              secondary={`${detail.data.action_items.pending} open`}
              testId={`admin-user-actions-${user.id}`}
            />
          </div>

          {/* Recent sessions */}
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-2">
              Recent sessions
            </p>
            {detail.data.recent_sessions.length === 0 ? (
              <div className="flex flex-col gap-3">
                <p className="text-xs text-[#666] italic">No sessions yet.</p>
                <FirstSessionNudgeButton userId={user.id} userEmail={user.email} />
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-[#1a1a1a] border border-[#1a1a1a] rounded-md overflow-hidden">
                {detail.data.recent_sessions.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#111]">
                    <FileText size={13} className="text-[#666] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{s.title}</p>
                      <p className="text-[11px] font-mono text-[#666]">
                        {fmtDate(s.created_at)}
                        {s.duration_seconds > 0 && (
                          <> · {Math.round(s.duration_seconds / 60)} min</>
                        )}
                        {" · "}
                        <span className={s.status === "completed" ? "text-[#c89f45]" : "text-[#888]"}>
                          {s.status}
                        </span>
                      </p>
                    </div>
                    {s.has_minutes && (
                      <Link
                        to={`/meetings/${s.id}/minutes`}
                        className="text-[11px] font-mono uppercase tracking-widest text-[#c89f45] hover:underline shrink-0"
                        data-testid={`admin-user-session-minutes-${s.id}`}
                      >
                        Minutes
                      </Link>
                    )}
                    <Link
                      to={`/meetings/${s.id}`}
                      className="text-[11px] font-mono uppercase tracking-widest text-[#888] hover:text-[#c89f45] shrink-0"
                      data-testid={`admin-user-session-open-${s.id}`}
                    >
                      Open
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function RollupChip({ icon: Icon, label, value, secondary, testId }) {
  return (
    <div
      className="flex items-center gap-3 border border-[#1a1a1a] rounded-md px-3 py-2 bg-[#0a0a0a]"
      data-testid={testId}
    >
      <Icon size={14} className="text-[#c89f45]" />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#666]">
          {label}
        </span>
        <span className="text-sm font-mono text-[#eaeaea]">
          {value}
          {secondary && (
            <span className="text-[11px] text-[#666] ml-1.5">· {secondary}</span>
          )}
        </span>
      </div>
    </div>
  );
}


/**
 * Single-user "Send first-session reminder" button. Rendered inside the
 * expanded detail panel when the user has zero sessions. Simple loading /
 * success / failed states — no confirm dialog since it's low-risk (just an
 * email) and admins get quick feedback via toast.
 */
function FirstSessionNudgeButton({ userId, userEmail }) {
  const [state, setState] = useState("idle"); // idle | sending | sent | failed

  const send = async () => {
    setState("sending");
    try {
      const r = await api.post("/admin/nudge/first-session", { user_ids: [userId] });
      if (r.data.sent === 1) {
        setState("sent");
        toast.success(`Reminder sent to ${userEmail}`);
      } else {
        setState("failed");
        toast.error("Send failed — check Resend logs.");
      }
    } catch (e) {
      setState("failed");
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not send reminder.");
    }
  };

  const isSent = state === "sent";
  const isSending = state === "sending";
  const isFailed = state === "failed";
  return (
    <button
      type="button"
      onClick={send}
      disabled={isSending || isSent}
      data-testid={`admin-nudge-single-${userId}`}
      className={`self-start inline-flex items-center gap-2 rounded-md text-xs px-3 py-2 border transition-colors ${
        isSent
          ? "border-[#c89f45]/40 bg-[#c89f45]/10 text-[#c89f45] cursor-default"
          : isFailed
          ? "border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20"
          : "border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45]/10"
      } disabled:opacity-60`}
    >
      <Send size={12} />
      {isSending ? "Sending…" : isSent ? "Sent · they'll see it in inbox" : isFailed ? "Send failed · retry" : "Send first-session reminder"}
    </button>
  );
}


/**
 * Bulk "Nudge every user who's never started" button. Backend applies a
 * 7-day cooldown per user so hitting this repeatedly is safe and idempotent.
 */
function BulkNudgeButton({ onDone }) {
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!window.confirm("Send the first-session reminder to every user who has never held a session? (Users nudged in the past 7 days are automatically skipped.)")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/nudge/first-session", { all_never_started: true });
      const { targeted, sent, failed } = r.data;
      if (sent === 0 && targeted === 0) {
        toast.info("No one to nudge — either everyone's held a session, or every eligible user was nudged in the last 7 days.");
      } else {
        toast.success(`Nudged ${sent}/${targeted}${(failed || []).length ? ` · ${failed.length} failed` : ""}`);
      }
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Bulk nudge failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={send}
      disabled={busy}
      className="inline-flex items-center gap-2 rounded-md text-xs px-3 py-2 border border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45]/10 transition-colors disabled:opacity-60 shrink-0"
      data-testid="admin-nudge-bulk"
    >
      <Send size={12} />
      {busy ? "Nudging…" : "Nudge users w/o sessions"}
    </button>
  );
}
